/**
 * Crypto utility functions for PDF decryption
 * Matches the C# backend encryption implementation using AES
 */

import CryptoJS from "crypto-js";

// This should match the encryption key used in the backend
// In a real application, this would be securely managed via environment variables
// or fetched securely from a configuration endpoint
const ENCRYPTION_KEY =
  process.env.VUE_APP_ENCRYPTION_KEY || "your-encryption-key-here";

/**
 * Decrypts byte array that was encrypted using the backend EncryptBytes method
 * @param {ArrayBuffer} encryptedData - The encrypted data as ArrayBuffer
 * @returns {ArrayBuffer} - The decrypted data
 */
export function decryptBytes(encryptedData) {
  try {
    console.log(
      "Starting PDF decryption, data length:",
      encryptedData.byteLength
    );

    // Convert ArrayBuffer to Uint8Array
    const encryptedArray = new Uint8Array(encryptedData);

    // Validate minimum data length (IV + some content)
    if (encryptedArray.length < 16) {
      throw new Error(
        "Encrypted data too short to contain valid IV and content"
      );
    }

    // Extract IV (first 15 bytes as per backend implementation)
    const iv = encryptedArray.slice(0, 15);
    console.log("Extracted IV length:", iv.length);

    // Extract encrypted content (remaining bytes)
    const encryptedContent = encryptedArray.slice(15);
    console.log("Encrypted content length:", encryptedContent.length);

    // Convert IV to WordArray for PBKDF2 salt
    const saltWordArray = CryptoJS.lib.WordArray.create(iv);

    // Convert encrypted content to WordArray
    const encryptedWordArray = CryptoJS.lib.WordArray.create(encryptedContent);

    // Derive key using PBKDF2 (matches Rfc2898DeriveBytes)
    // C# Rfc2898DeriveBytes default is 1000 iterations
    const key = CryptoJS.PBKDF2(ENCRYPTION_KEY, saltWordArray, {
      keySize: 256 / 32, // 32 bytes = 256 bits
      iterations: 1000,
      hasher: CryptoJS.algo.SHA1, // Rfc2898DeriveBytes uses SHA1
    });

    // Derive IV using PBKDF2 (16 bytes for AES)
    const derivedIv = CryptoJS.PBKDF2(ENCRYPTION_KEY, saltWordArray, {
      keySize: 128 / 32, // 16 bytes = 128 bits
      iterations: 1000,
      hasher: CryptoJS.algo.SHA1, // Rfc2898DeriveBytes uses SHA1
    });

    console.log("Key and IV derived successfully");

    // Decrypt the data
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encryptedWordArray },
      key,
      {
        iv: derivedIv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    // Validate decryption result
    if (!decrypted || decrypted.sigBytes <= 0) {
      throw new Error("Decryption failed - no valid data returned");
    }

    console.log("Decryption completed, output size:", decrypted.sigBytes);

    // Convert back to ArrayBuffer with improved method
    const decryptedArray = new Uint8Array(decrypted.sigBytes);
    const words = decrypted.words;

    // More robust byte extraction
    for (let i = 0; i < decrypted.sigBytes; i++) {
      const wordIndex = Math.floor(i / 4);
      const byteIndex = 3 - (i % 4); // Big-endian to little-endian conversion
      if (wordIndex < words.length) {
        decryptedArray[i] = (words[wordIndex] >>> (byteIndex * 8)) & 0xff;
      }
    }

    // Validate PDF header
    const pdfHeader = decryptedArray.slice(0, 4);
    const expectedHeader = new Uint8Array([0x25, 0x50, 0x44, 0x46]); // "%PDF"

    if (!pdfHeader.every((byte, index) => byte === expectedHeader[index])) {
      console.warn(
        "PDF header validation failed. Expected %PDF, got:",
        String.fromCharCode(...pdfHeader)
      );
      // Don't throw error here as some PDFs might have different headers
    } else {
      console.log("PDF header validation passed");
    }

    return decryptedArray.buffer;
  } catch (error) {
    console.error("Decryption failed:", error);
    console.error("Error details:", {
      message: error.message,
      stack: error.stack,
      dataLength: encryptedData ? encryptedData.byteLength : "undefined",
    });
    throw new Error(`Failed to decrypt PDF data: ${error.message}`);
  }
}

/**
 * Creates a blob URL from decrypted PDF data
 * @param {ArrayBuffer} decryptedData - The decrypted PDF data
 * @returns {string} - The blob URL
 */
export function createPdfBlobUrl(decryptedData) {
  try {
    // Additional validation before creating blob
    if (!decryptedData || decryptedData.byteLength === 0) {
      throw new Error("No data provided for PDF blob creation");
    }

    console.log("Creating PDF blob, data size:", decryptedData.byteLength);

    const blob = new Blob([decryptedData], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);

    console.log("PDF blob URL created successfully:", blobUrl);
    return blobUrl;
  } catch (error) {
    console.error("Failed to create PDF blob URL:", error);
    throw error;
  }
}

/**
 * Validates PDF data structure
 * @param {ArrayBuffer} data - The PDF data to validate
 * @returns {boolean} - True if data appears to be a valid PDF
 */
export function validatePdfData(data) {
  try {
    if (!data || data.byteLength < 8) {
      return false;
    }

    const header = new Uint8Array(data.slice(0, 8));
    const pdfSignature = [0x25, 0x50, 0x44, 0x46]; // "%PDF"

    // Check if it starts with %PDF
    for (let i = 0; i < 4; i++) {
      if (header[i] !== pdfSignature[i]) {
        return false;
      }
    }

    // Check for version number after %PDF-
    if (header[4] === 0x2d) {
      // "-"
      const version = String.fromCharCode(header[5]);
      return /[0-9]/.test(version);
    }

    return false;
  } catch (error) {
    console.error("PDF validation error:", error);
    return false;
  }
}

/**
 * Debug function to analyze encrypted data structure
 * @param {ArrayBuffer} encryptedData - The encrypted data to analyze
 * @returns {Object} - Analysis results
 */
export function analyzeEncryptedData(encryptedData) {
  const analysis = {
    totalSize: encryptedData.byteLength,
    hasMinimumSize: encryptedData.byteLength >= 16,
    ivSize: 15,
    contentSize: encryptedData.byteLength - 15,
    firstBytes: [],
    lastBytes: [],
  };

  const data = new Uint8Array(encryptedData);

  // Get first 20 bytes for analysis
  analysis.firstBytes = Array.from(data.slice(0, Math.min(20, data.length)));

  // Get last 20 bytes for analysis
  if (data.length > 20) {
    analysis.lastBytes = Array.from(data.slice(-20));
  }

  return analysis;
}

/**
 * Revokes a blob URL to free up memory
 * @param {string} blobUrl - The blob URL to revoke
 */
export function revokeBlobUrl(blobUrl) {
  if (blobUrl && blobUrl.startsWith("blob:")) {
    URL.revokeObjectURL(blobUrl);
  }
}
